import { useEffect, useRef } from 'react';
import { queueCurrent, queueHoldsVideo, queueNeighbours } from '@visious/core';
import { navigate } from '../router/router';
import { queueEntryHref } from './model';
import { advanceQueue, clearQueue, followQueueVideo, readQueue, useQueue } from './store';

/**
 * The three rules that keep the queue and the address bar telling the same story.
 *
 * **On `/watch`, the route is the truth.** Arriving at a video — by pressing a queue row, by the back
 * button, by a pasted link — is what makes it the queue's current entry, so the cursor follows the
 * route. That is what lets a queue row be a plain `/watch?v=` link with no queue-specific handling on
 * it, which in turn is what keeps middle-click, ⌘-click and "copy link address" working on every row.
 *
 * **Off `/watch`, the cursor is the truth.** The corner player's target *is* the cursor's entry
 * (`playback/PlaybackHost.tsx`), so advancing the queue there is a change of state and not a
 * navigation: the host re-keys on the new video and the next entry loads in the corner. This is the
 * difference from a list that advances by navigating — a corner player that yanked the whole app to the
 * next video would be the least minimized thing on screen — and a list that stopped at the end of every
 * video by default would not be a queue. Stopping there is the reader's own call, made with the panel's
 * Autoplay switch, and it holds in the corner as well as on the page: one gate on the advance below
 * covers both, since both arrive here.
 *
 * **Playing something else is leaving the queue**, and the queue is discarded rather than kept
 * waiting — see the arrival rule below.
 */
export function useQueueAdvance(input: {
  readonly videoId: string;
  /** The route's `list`, which is the queue's own playlist while the queue is a window on one. */
  readonly playlistId: string | null;
  readonly ended: boolean;
  /** `false` on /embed only, which drives no queue and must not edit one either. */
  readonly enabled: boolean;
  /** The reader is on `/watch` looking at this video, rather than at the corner player. */
  readonly onRoute: boolean;
  /**
   * The list's "play the next one when this one ends" (`platform/watchLayout.ts`), already resolved to
   * whichever of the two stored switches this list keeps (`useWatchScreen`'s `listAutoplay`). It gates
   * the **advance** and nothing else below: with it off the list stops at the end of every video, here
   * and in the corner player alike, and the reader moves it on themselves with a row, the panel's
   * forward button or the corner player's.
   */
  readonly autoplay: boolean;
}): void {
  const { videoId, playlistId, ended, enabled, onRoute, autoplay } = input;
  const state = useQueue();
  /**
   * Whether this route and this queue are about the same list. A queue of the reader's own is driven by
   * a plain `/watch?v=`; a queue that is a window on a playlist is driven by that playlist's own route,
   * and a `list` naming some *other* playlist is a list this queue is not — the panel's loader answers
   * that arrival by reading the new playlist in (`screens/watch/useWatchPlaylist.ts`).
   */
  const link = state.link?.playlistId ?? null;
  const driving = enabled && (playlistId === null ? link === null : playlistId === link);

  /*
   * **Arriving at a video the queue is not about discards the queue.** A queue is the list this tab is
   * watching *through*, so playing a video that was never in it ends the list rather than parking it. The
   * alternative is a queue that outlives every detour and resurfaces in the corner an hour later, next to
   * a video that has nothing to do with it.
   *
   * **A `list` arrival is not this rule's to answer.** That address names a playlist, and a playlist is
   * what the queue becomes: the loader projects it in, and it is also the one that clears a queue that is
   * some other list's, since it is the only thing that knows whether the playlist has been read yet.
   *
   * **Once per arrival**, through a ref keyed by the route rather than on every render, and that is what
   * makes the rule survive the way a queue is *built*: "add the video I am watching plus this one"
   * creates a queue under an unchanged watch route, and a rule re-asserted on every render would delete
   * it in the same frame it was made. The ref is enough on its own — the host keys the whole session by
   * video id, so a new video arrives on a fresh mount — but the route can gain a `list` without changing
   * video, so the key carries both. It carries them as JSON rather than joined by a separator, because
   * `v` and `list` are both unvalidated: any character a separator could use is a character one of the
   * two ids may contain, and then two different routes share one key.
   */
  const decidedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !onRoute) return;
    const arrival = JSON.stringify([videoId, playlistId]);
    if (decidedForRef.current === arrival) return;
    decidedForRef.current = arrival;
    // The live snapshot, not the render's: this asks about the queue at the instant of the arrival.
    if (playlistId === null && !queueHoldsVideo(readQueue(), videoId)) clearQueue();
  }, [enabled, onRoute, playlistId, videoId]);

  /*
   * The route → cursor half. Guarded on the queue holding the video at all (`queueFollowVideo`
   * declines otherwise), which after the rule above can only be true.
   *
   * Only while the reader is *on* the route: off it the route is stale by definition — the address
   * bar still names whatever page they navigated to — and following it would drag the cursor back to
   * a video the corner player has already left.
   */
  useEffect(() => {
    if (!driving || !onRoute) return;
    followQueueVideo(videoId);
    // `state` is a dependency rather than only `videoId`: the queue can grow under a watch page —
    // "add the video I am watching plus this one" is one of the two ways a queue is created, and a
    // playlist's later pages are another — and the cursor has to land on the row that arrival made, not
    // wait for the next navigation.
  }, [driving, onRoute, state, videoId]);

  /*
   * The advance. Guarded on `ended` for the playlist arm's reason — a video paused on its last frame
   * reports the same numbers — and once per ending through a ref keyed by video id, because the
   * transport is polled four times a second and every tick between the end and the state actually
   * changing would otherwise fire again. The ref never needs clearing: the host keys the whole
   * session by video id, so the next entry arrives on a fresh mount with a fresh transport, which is
   * also what stops one ending from cascading through the rest of the queue in a single frame.
   */
  const advancedFromRef = useRef<string | null>(null);
  useEffect(() => {
    if (!driving || !ended) return;
    // Above the ref, deliberately: a switched-off autoplay must leave no trace of this ending, or
    // switching it back on mid-video would find the ending already "answered" and stop the list here.
    if (!autoplay) return;
    if (advancedFromRef.current === videoId) return;
    // The live snapshot rather than the render's: an ending is answered from the queue as it stands
    // at that instant, which may be several edits past the one this effect closed over.
    const live = readQueue();
    const current = queueCurrent(live);
    if (current === null) return;
    advancedFromRef.current = videoId;

    /*
     * **The cursor has already moved on, so play the row it moved to.** This is the ending of a video the
     * list no longer holds, and the list does hold a row: removing the playing row advances the queue
     * (`core/queue/model.ts`), so the cursor sits on the successor while the address still names the video
     * that was taken out — which then plays to its end with nothing following it. Reachable by hand, from
     * the panel's per-row remove on the row that is playing, and it is also what a removal mirrored to a
     * linked playlist leaves behind, where §7i's renumbering puts the successor at the same `index`.
     */
    if (current.videoId !== videoId) {
      if (onRoute) navigate(queueEntryHref(current, live.link));
      return;
    }

    const next = queueNeighbours(live).next;
    // `advanceQueue` either way, so the cursor lands on the next entry — or, at the end of the list,
    // on nothing. Reaching the end of a queue is an ending: the list stays and the corner player
    // goes, rather than the queue looping back to its first video (`core/queue/model.ts`).
    advanceQueue();
    // On the route the page has to follow the cursor, or the reader would be left looking at a
    // finished video while the corner player they cannot see plays the next one.
    if (next !== null && onRoute) navigate(queueEntryHref(next, live.link));
  }, [autoplay, driving, ended, onRoute, videoId]);
}
